import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Table, Spinner, Button, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getMaterialsStock, getMaterialsList, bulkUpdateMaterialsStock, createOrUpdateMaterialStock } from '../../../services/MaterialsStockService';
import { isConvertible, convert, getUnitPair, formatUnitForDisplay } from '../../../utils/unitConversion';
import { getMaterialName } from '../../../utils/materialName';

export default function MaterialsStock() {
    const { t, i18n } = useTranslation();
    const [materialsList, setMaterialsList] = useState([]); // Array of {name, unit} from material logs
    const [materialsMap, setMaterialsMap] = useState({}); // { material_name: { unit, material } } map for quick lookup
    const [stockItems, setStockItems] = useState([]); // Current stock entries
    const [loading, setLoading] = useState(true);
    const [quantities, setQuantities] = useState({}); // { material_name: quantity }
    const [units, setUnits] = useState({}); // { material_name: unit }
    const [searchQuery, setSearchQuery] = useState('');
    const [savingMaterials, setSavingMaterials] = useState({}); // Track which materials are being saved
    const saveTimeouts = useRef({}); // Store timeouts for debouncing

    const load = async () => {
        try {
            setLoading(true);
            const [materialsRes, stockRes] = await Promise.all([
                getMaterialsList().catch(() => ({ data: [] })),
                getMaterialsStock().catch(() => ({ data: [] })),
            ]);
            
            const materials = materialsRes.data || []; // Array of {name, unit}
            const stock = stockRes.data || [];
            
            // Create materials map for quick lookup
            const matMap = {};
            const matNames = [];
            materials.forEach(mat => {
                const name = typeof mat === 'string' ? mat : mat.name;
                const unit = typeof mat === 'string' ? 'unit' : (mat.unit || 'unit');
                matMap[name] = {
                    unit,
                    material: typeof mat === 'string'
                        ? { material_name: name }
                        : { material_name: mat.name, name_nl: mat.name_nl },
                };
                matNames.push(name);
            });
            
            setMaterialsList(matNames);
            setMaterialsMap(matMap);
            setStockItems(stock);
            
            // Initialize quantities and units
            const qtyMap = {};
            const unitMap = {};
            
            // First, use units from existing stock
            stock.forEach(item => {
                qtyMap[item.material_name] = item.quantity || 0;
                // Use stock unit if it exists and is valid, otherwise fall back to Material Logs unit
                const stockUnit = item.unit || 'unit';
                const materialLogUnit = matMap[item.material_name]?.unit || 'unit';
                unitMap[item.material_name] = stockUnit !== 'unit' ? stockUnit : materialLogUnit;
            });
            
            // Then, use units from material logs for materials not in stock yet
            matNames.forEach(matName => {
                if (!(matName in unitMap)) {
                    unitMap[matName] = matMap[matName]?.unit || 'unit';
                }
            });
            
            setQuantities(qtyMap);
            setUnits(unitMap);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('materialsStock.errors.load');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filteredMaterials = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return materialsList;
        return materialsList.filter((name) => {
            const material = materialsMap[name]?.material || { material_name: name };
            const combined = `${name} ${material.name_nl || ''}`.toLowerCase();
            return combined.includes(query);
        });
    }, [materialsList, materialsMap, searchQuery, i18n.resolvedLanguage]);

    // Auto-save function with debouncing
    const autoSaveMaterial = useCallback(async (materialName, quantity, unit) => {
        // Clear existing timeout for this material
        if (saveTimeouts.current[materialName]) {
            clearTimeout(saveTimeouts.current[materialName]);
        }

        // Set saving state
        setSavingMaterials(prev => ({ ...prev, [materialName]: true }));

        // Debounce: wait 1 second after last change before saving
        saveTimeouts.current[materialName] = setTimeout(async () => {
            try {
                const qty = parseFloat(quantity) || 0;
                await createOrUpdateMaterialStock({
                    material_name: materialName,
                    quantity: qty,
                    unit: unit || 'unit'
                });
                // Success - update stock items to reflect saved state
                setStockItems(prev => {
                    const updated = [...prev];
                    const existingIndex = updated.findIndex(s => s.material_name === materialName);
                    if (existingIndex >= 0) {
                        updated[existingIndex] = {
                            ...updated[existingIndex],
                            quantity: qty,
                            unit: unit || 'unit'
                        };
                    } else {
                        updated.push({
                            material_name: materialName,
                            quantity: qty,
                            unit: unit || 'unit'
                        });
                    }
                    return updated;
                });
            } catch (err) {
                const msg = err.response?.data?.error || err.message || t('materialsStock.errors.save');
                toast.error(t('materialsStock.errors.saveMaterial', { name: materialName, message: msg }));
            } finally {
                setSavingMaterials(prev => {
                    const updated = { ...prev };
                    delete updated[materialName];
                    return updated;
                });
                delete saveTimeouts.current[materialName];
            }
        }, 1000); // 1 second debounce
    }, []);

    const handleQuantityChange = (materialName, value) => {
        const qty = value === '' ? '' : parseFloat(value) || 0;
        setQuantities(prev => ({
            ...prev,
            [materialName]: qty
        }));
        
        // Auto-save after change
        const unit = units[materialName] || 'unit';
        autoSaveMaterial(materialName, qty, unit);
    };

    const handleUnitChange = (materialName, newUnit) => {
        // Convert quantity when unit changes
        const currentQuantity = quantities[materialName] || 0;
        const currentUnit = units[materialName] || 'unit';
        
        let convertedQuantity = currentQuantity;
        if (isConvertible(currentUnit) && isConvertible(newUnit)) {
            convertedQuantity = convert(currentQuantity, currentUnit, newUnit);
        }
        
        setUnits(prev => ({
            ...prev,
            [materialName]: newUnit
        }));
        
        setQuantities(prev => ({
            ...prev,
            [materialName]: convertedQuantity
        }));
        
        // Auto-save after unit change
        autoSaveMaterial(materialName, convertedQuantity, newUnit);
    };

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            Object.values(saveTimeouts.current).forEach(timeout => {
                if (timeout) clearTimeout(timeout);
            });
        };
    }, []);

    const getStockQuantity = (materialName) => {
        const stockItem = stockItems.find(s => s.material_name === materialName);
        return stockItem ? stockItem.quantity : 0;
    };

    const getStockUnit = (materialName) => {
        const stockItem = stockItems.find(s => s.material_name === materialName);
        // If stock exists, use its unit; otherwise use unit from Material Logs
        if (stockItem) {
            return stockItem.unit || 'unit';
        }
        // Fall back to Material Logs unit
        return materialsMap[materialName]?.unit || 'unit';
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">{t('materialsStock.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <h4 className="heading mb-0">{t('materialsStock.title')}</h4>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('materialsStock.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </Col>
                            </Row>

                            {filteredMaterials.length === 0 ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">{t('materialsStock.noMaterialsFound')}</p>
                                    <p className="text-muted small">{t('materialsStock.addMaterialsFirst')}</p>
                                </div>
                            ) : (
                                <Table responsive bordered className="materials-stock-table">
                                    <thead>
                                        <tr>
                                            <th>{t('materialsStock.columns.materialName')}</th>
                                            <th>{t('materialsStock.columns.unit')}</th>
                                            <th>{t('materialsStock.columns.quantity')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMaterials.map((materialName) => {
                                            const currentStock = getStockQuantity(materialName);
                                            const materialLogUnit = materialsMap[materialName] || 'unit'; // Unit from Material Logs
                                            const stockUnit = getStockUnit(materialName); // Unit from stock or Material Logs
                                            const quantity = quantities[materialName] !== undefined ? quantities[materialName] : currentStock;
                                            // Use unit from state, or stock, or Material Logs as fallback
                                            const unit = units[materialName] || stockUnit || materialLogUnit;

                                            return (
                                                <tr key={materialName}>
                                                    <td><strong>{getMaterialName(materialsMap[materialName]?.material || { material_name: materialName })}</strong></td>
                                                    <td>
                                                        {isConvertible(unit) ? (
                                                            <Form.Select
                                                                size="sm"
                                                                value={unit}
                                                                onChange={(e) => handleUnitChange(materialName, e.target.value)}
                                                                style={{ minWidth: 100 }}
                                                            >
                                                                {(() => {
                                                                    const unitPair = getUnitPair(unit);
                                                                    if (unitPair) {
                                                                        const [smallerUnit, largerUnit] = unitPair;
                                                                        return (
                                                                            <>
                                                                                <option value={smallerUnit}>{formatUnitForDisplay(smallerUnit)}</option>
                                                                                <option value={largerUnit}>{formatUnitForDisplay(largerUnit)}</option>
                                                                            </>
                                                                        );
                                                                    }
                                                                    return <option value={unit}>{formatUnitForDisplay(unit)}</option>;
                                                                })()}
                                                            </Form.Select>
                                                        ) : (
                                                            <Form.Control
                                                                type="text"
                                                                size="sm"
                                                                value={unit}
                                                                onChange={(e) => handleUnitChange(materialName, e.target.value)}
                                                                placeholder="unit"
                                                                style={{ minWidth: 100 }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Form.Control
                                                                type="number"
                                                                step="0.0001"
                                                                min="0"
                                                                size="sm"
                                                                value={quantity === '' ? '' : quantity}
                                                                onChange={(e) => handleQuantityChange(materialName, e.target.value)}
                                                                placeholder="0"
                                                                style={{ 
                                                                    minWidth: 150,
                                                                    borderColor: (quantity === 0 || quantity === '') ? '#dc3545' : undefined,
                                                                    backgroundColor: (quantity === 0 || quantity === '') ? '#fff5f5' : undefined
                                                                }}
                                                                className={quantity === 0 || quantity === '' ? 'border-danger' : ''}
                                                            />
                                                            {savingMaterials[materialName] && (
                                                                <Spinner animation="border" size="sm" className="text-primary" />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}

                            {filteredMaterials.length > 0 && (
                                <div className="mt-3">
                                    <div className="text-muted small">
                                        {t('common.showingResults', { shown: filteredMaterials.length, total: materialsList.length, label: 'materials' })}
                                        <span className="ms-2 text-success">
                                            <i className="fa-solid fa-check-circle me-1" />
                                            {t('common.autoSaved')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
